import type { ErrorResponse } from "@apollo/client/link/error"
import { captureException, withScope } from "@sentry/react"
import config from "@/v2-backoffice-client/config"

/**
 * Reports GraphQL errors to Sentry with consistent fingerprinting that
 * mirrors the server-side error presenter. The server attaches
 * `extensions.module` and `extensions.code` to every GraphQL error; we
 * use those to build fingerprints that group client-side reports the
 * same way the backend groups its own.
 */
export function reportGraphQLErrors(props: ErrorResponse) {
  const { networkError, graphQLErrors, operation } = props

  if (networkError) {
    withScope((scope) => {
      scope.setTag("graphql.operationName", operation.operationName)
      scope.setTag("type", "network")
      scope.setFingerprint(["graphql", "network", operation.operationName])
      captureException(networkError)
    })
  }

  if (!graphQLErrors) return

  for (const error of graphQLErrors) {
    withScope((scope) => {
      const { operationName, variables } = operation
      const module =
        (error.extensions?.module as string | undefined) ?? "unknown"
      const code = (error.extensions?.code as number | undefined) ?? 500

      const ctx: Record<string, unknown> = { operationName, code, module }
      if (variables) ctx.variables = JSON.stringify(variables)
      if (error.path) ctx.path = error.path.join(" > ")

      const fingerprint = ["graphql", module, operationName]

      scope.setContext("GraphQL", ctx)
      scope.setTag("graphql.operationName", operationName)
      scope.setTag("module", module)
      scope.setTransactionName(operationName)
      scope.setFingerprint(fingerprint)

      captureException(new Error(error.message))
    })
  }
}

/**
 * Logs GraphQL errors to the console. Used when Sentry instrumentation
 * is disabled (local development).
 */
export function logGraphQLErrors(props: ErrorResponse) {
  const { networkError, graphQLErrors } = props

  if (networkError) {
    // eslint-disable-next-line no-console
    console.error("[Network error]:", networkError)
  }
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      // eslint-disable-next-line no-console
      console.error(
        `[GraphQL error]: ${error.message}`,
        error.extensions,
        error.path,
      )
    }
  }
}

/**
 * Combined handler — delegates to Sentry or console depending on
 * whether instrumentation is enabled.
 */
export function handleGraphQLErrors(props: ErrorResponse) {
  if (config.isInstrumentationEnabled) {
    reportGraphQLErrors(props)
  } else {
    logGraphQLErrors(props)
  }
}
